import { useState, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import type { UserDetail } from 'modules/account/services/useGetAccountInfo';
import type { RolesToAssign } from 'modules/account/types';
import type { Password } from 'modules/account/services/useUpdatePassword';
import {
  buildAccountUpdatePayload,
  buildRolesAssignmentPayload,
  allPromisesSettledSuccessfully,
} from 'modules/account/detail/helpers/accountDetailHandlers';
import useUpdateAccountInfo from 'modules/account/services/useUpdateAccountInfo';
import useUpdatePassword from 'modules/account/services/useUpdatePassword';
import useUpdateRoles from 'modules/account/services/useUpdateRoles';

interface UseAccountDetailUpdateParams {
  accountId: string;
  userInfo: UserDetail;
  rolesToAssign: RolesToAssign;
  password: Password;
  toPreviousPage: () => void;
}

export function useAccountDetailUpdate(params: UseAccountDetailUpdateParams) {
  const { accountId, userInfo, rolesToAssign, password, toPreviousPage } = params;
  const [isSaving, setIsSaving] = useState(false);
  const { formatMessage } = useIntl();
  const updateAccount = useUpdateAccountInfo();
  const updatePassword = useUpdatePassword();
  const updateRoles = useUpdateRoles();

  const handleUpdate = useCallback(async () => {
    const userId = accountId || userInfo.id;
    if (!userId) return;

    setIsSaving(true);
    try {
      const accountPayload = buildAccountUpdatePayload({ ...userInfo, id: userId });
      const rolesAssignment = buildRolesAssignmentPayload(rolesToAssign);

      const promises: Promise<unknown>[] = [
        updateAccount(accountPayload),
        updateRoles({ userId, rolesAssignment }),
      ];
      if (password.password) {
        promises.push(updatePassword({ userId, newPassword: password.password }));
      }

      const results = await Promise.allSettled(promises);
      if (!allPromisesSettledSuccessfully(results)) return;

      enqueueSnackbar({ message: formatMessage({ id: 'updateSuccess' }), variant: 'success' });
      toPreviousPage();
    } finally {
      setIsSaving(false);
    }
  }, [
    accountId,
    userInfo,
    rolesToAssign,
    password.password,
    toPreviousPage,
    formatMessage,
    updateAccount,
    updateRoles,
    updatePassword,
  ]);

  return { handleUpdate, isSaving };
}
